import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { AdminPanelComponent, NativeStorageService, PurchasingService } from 'army-builder-shared';
import { AddPurchase, RemovePurchase } from 'army-builder-shared/src/lib/global/purchase/service/state';
import { combineLatest, map } from 'rxjs';

const STORAGE_KEY = 'admin_entitlement_overrides';

/**
 * Admin-only collapsible panel that lists all card packs and lets an admin
 * toggle which ones they "own" in the current session.
 *
 * Overrides are persisted in NativeStorageService and restored on init.
 * A "Reset Overrides" button clears them and re-syncs from RevenueCat.
 */
@Component({
    standalone: true,
    selector: 'admin-entitlement-panel',
    imports: [CommonModule, IonicModule, AdminPanelComponent],
    encapsulation: ViewEncapsulation.None,
    template: `
        <abs-admin-panel label="Admin: Entitlements">
            @if (packs$ | async; as packs) {
                @for (pack of packs; track pack.key) {
                    <div class="admin-panel-row">
                        <span class="admin-panel-row-label">{{ pack.key }}</span>
                        <ion-toggle [checked]="pack.owned" (ionChange)="togglePack(pack.key, $event)"></ion-toggle>
                    </div>
                }
            }

            <div class="admin-panel-actions">
                <button class="admin-panel-btn" (click)="resetOverrides()">
                    <ion-icon name="refresh-outline"></ion-icon>
                    Reset Overrides
                </button>
            </div>
        </abs-admin-panel>
    `
})
export class AdminEntitlementPanelComponent implements OnInit {
    overrides: string[] = [];

    /** All NON_CONSUMABLE products with their current owned status */
    packs$ = combineLatest([this.purchasingSvc.products$, this.purchasingSvc.purchases$]).pipe(
        map(([products, purchases]) =>
            products
                .filter((p) => p.type === 'NON_CONSUMABLE' || p.key === 'all_access')
                .map((p) => ({
                    key: p.key,
                    owned: purchases.includes(p.key) || p.bundles?.some((b) => purchases.includes(b))
                }))
        )
    );

    constructor(
        private store: Store,
        private purchasingSvc: PurchasingService,
        private storage: NativeStorageService
    ) {}

    async ngOnInit() {
        const saved = await this.storage.getItem(STORAGE_KEY, []);
        if (Array.isArray(saved) && saved.length > 0) {
            this.overrides = saved;
            for (const productId of this.overrides) {
                this.store.dispatch(AddPurchase({ productId }));
            }
        }
    }

    togglePack(productId: string, event: CustomEvent) {
        const checked = event.detail.checked;
        if (checked) {
            this.store.dispatch(AddPurchase({ productId }));
            if (!this.overrides.includes(productId)) {
                this.overrides = [...this.overrides, productId];
            }
        } else {
            this.store.dispatch(RemovePurchase({ productId }));
            this.overrides = this.overrides.filter((k) => k !== productId);
        }
        this.storage.setItem(STORAGE_KEY, this.overrides);
    }

    async resetOverrides() {
        for (const productId of this.overrides) {
            this.store.dispatch(RemovePurchase({ productId }));
        }
        this.overrides = [];
        await this.storage.setItem(STORAGE_KEY, []);
        await this.purchasingSvc.refreshUserPurchases();
    }
}
